/**
 * ProgressRing — anel SVG animado pra %.
 */
import { useEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";

interface Props {
  value: number; // 0-100
  size?: number;
  strokeWidth?: number;
  color?: "primary" | "success" | "warning" | "danger";
  className?: string;
  label?: string;
}

const COLORS = {
  primary: "stroke-sky-500",
  success: "stroke-emerald-500",
  warning: "stroke-amber-500",
  danger: "stroke-rose-500",
};

export function ProgressRing({
  value,
  size = 96,
  strokeWidth = 8,
  color = "primary",
  className,
  label,
}: Props) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const [animatedValue, setAnimatedValue] = useState(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const start = animatedValue;
    const target = Math.max(0, Math.min(100, value));
    const duration = 1200;
    let startTime: number | null = null;

    const tick = (ts: number) => {
      if (startTime === null) startTime = ts;
      const elapsed = ts - startTime;
      const t = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(2, -10 * t); // ease-out-expo
      setAnimatedValue(start + (target - start) * eased);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const offset = circumference - (animatedValue / 100) * circumference;

  return (
    <div className={cn("relative inline-flex items-center justify-center", className)}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          className="stroke-slate-200 dark:stroke-white/10 fill-none"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          className={cn("fill-none transition-all", COLORS[color])}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={cn(
          "font-bold tabular-nums leading-none",
          size > 80 ? "text-xl" : "text-sm",
        )}>
          {Math.round(animatedValue)}%
        </span>
        {label && size > 80 && (
          <span className="text-[9px] uppercase tracking-wider text-muted-foreground mt-0.5">{label}</span>
        )}
      </div>
    </div>
  );
}
